import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  AlertTriangle, Plus, FileDown, Trash2, Edit, Upload, Camera, ClipboardList,
  Gavel, Save, X,
} from "lucide-react";

const FAULT_OPTIONS = ["Şoför kusurlu", "Karşı taraf kusurlu", "Ortak kusur", "Belirsiz"];
const STATUS_OPTIONS = ["Açık", "Sigortada", "Kapandı"];

const monthNow = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const emptyForm = () => ({
  date: monthNow(), time: "", vehicle_id: "", personnel_id: "",
  location: "", description: "",
  single_party: false,
  opposing: { plate: "", name: "", phone: "", insurance: "" },
  fault: "Belirsiz", damage_description: "", estimated_cost_tl: 0,
  status: "Açık", note: "",
});

const fmtTl = (n) => `₺ ${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const statusColor = (s) => ({
  "Açık": "bg-red-100 text-red-700",
  "Sigortada": "bg-amber-100 text-amber-700",
  "Kapandı": "bg-emerald-100 text-emerald-700",
}[s] || "bg-slate-100 text-slate-500");

export default function Accidents() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [driversForDate, setDriversForDate] = useState([]);
  const tutanakRef = useRef(null); const damageRef = useRef(null);

  const load = async () => {
    setBusy(true);
    try {
      const params = statusFilter !== "all" ? { status: statusFilter } : {};
      const [ac, veh] = await Promise.all([
        api.get("/accidents", { params }),
        api.get("/vehicles"),
      ]);
      setItems(ac.data || []);
      setVehicles(veh.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaza raporları yüklenemedi");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  // Driver list filtered by selected accident date
  useEffect(() => {
    if (!form.date) return;
    api.get("/drivers", { params: { filter: "all", role: "Şoför" } }).then(({ data }) => {
      const active = (data || []).filter((d) => {
        const periods = d.employment_periods || [];
        if (!periods.length) return false;
        return periods.some((p) => {
          if (p.leave_reason && !p.start_date && !p.end_date) return false;
          const s = p.start_date ? new Date(p.start_date) : new Date("1970-01-01");
          const e = p.end_date ? new Date(p.end_date) : new Date("2099-12-31");
          const dt = new Date(form.date);
          return dt >= s && dt <= e;
        });
      });
      setDriversForDate(active);
    }).catch(() => setDriversForDate([]));
  }, [form.date]);

  const openNew = () => { setForm(emptyForm()); setEditingId(null); setDialogOpen(true); };
  const openEdit = async (item) => {
    setEditingId(item.id);
    setForm({
      date: item.date, time: item.time || "",
      vehicle_id: item.vehicle_id, personnel_id: item.personnel_id,
      location: item.location || "", description: item.description || "",
      single_party: !!item.single_party,
      opposing: item.opposing || { plate: "", name: "", phone: "", insurance: "" },
      fault: item.fault, damage_description: item.damage_description || "",
      estimated_cost_tl: item.estimated_cost_tl || 0, status: item.status, note: item.note || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.date || !form.vehicle_id || !form.personnel_id || !form.description.trim()) {
      return toast.error("Tarih, araç, şoför ve açıklama zorunlu");
    }
    try {
      const payload = { ...form, estimated_cost_tl: parseFloat(form.estimated_cost_tl) || 0 };
      if (payload.single_party) payload.opposing = null;
      if (editingId) {
        await api.patch(`/accidents/${editingId}`, payload);
        toast.success("Kaza raporu güncellendi");
      } else {
        const { data } = await api.post("/accidents", payload);
        setEditingId(data.id);
        toast.success("Kaza raporu oluşturuldu");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kaydedilemedi");
    }
  };

  const uploadPhoto = async (file, kind) => {
    if (!file || !editingId) {
      if (!editingId) toast.error("Önce kaydı oluşturun");
      return;
    }
    try {
      const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
      await api.post(`/accidents/${editingId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotoğraf yüklendi");
      const { data } = await api.get(`/accidents/${editingId}`);
      // Reload the item
      await load();
      setItems((prev) => prev.map((x) => x.id === data.id ? data : x));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Yüklenemedi");
    }
  };

  const deletePhoto = async (aid, pid) => {
    if (!window.confirm("Fotoğraf silinsin mi?")) return;
    try {
      await api.delete(`/accidents/${aid}/photo/${pid}`);
      toast.success("Silindi");
      await load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`${item.vehicle_plate || "Bu"} kaza kaydı silinsin mi?`)) return;
    try {
      const { data } = await api.delete(`/accidents/${item.id}`);
      if (data?.warning) toast.warning(data.warning);
      else toast.success("Silindi");
      await load();
    } catch (e) { toast.error("Silinemedi"); }
  };

  const processDiscipline = async (item) => {
    if (item.violation_id) return toast.info("Bu kaza zaten disipline işlenmiş");
    if (item.fault !== "Şoför kusurlu") return toast.error("Sadece 'Şoför kusurlu' işlenebilir");
    if (!window.confirm(`${item.personnel_name} — Kusurlu Kaza (50 puan) disipline işlensin mi?`)) return;
    try {
      await api.post(`/accidents/${item.id}/process-to-discipline`);
      toast.success("Disipline işlendi (50 puan)");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İşlenemedi");
    }
  };

  const downloadPdf = async (item) => {
    try {
      const res = await api.get(`/accidents/${item.id}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `YAZTUR-Kaza-${item.vehicle_plate || item.id.slice(0,8)}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("PDF indirilemedi"); }
  };

  const editingItem = useMemo(() => items.find((x) => x.id === editingId), [items, editingId]);

  return (
    <div className="space-y-6" data-testid="accidents-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Disiplin & Prim</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900 flex items-center gap-2">
            <AlertTriangle className="w-8 h-8 text-red-600" /> Kaza Raporları
          </h1>
          <p className="mt-1 text-sm text-slate-500">Kaza ayrıntıları, tutanak & hasar fotoğrafları, PDF ve disipline işleme.</p>
        </div>
        <div className="flex gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px] h-10" data-testid="status-filter"><SelectValue placeholder="Durum" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Durumlar</SelectItem>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={openNew} className="bg-red-700 hover:bg-red-800 text-white" data-testid="new-accident-btn">
            <Plus className="w-4 h-4 mr-1" /> Yeni Kaza Raporu
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-3 py-2 text-xs uppercase">Tarih</th>
                <th className="text-left px-3 py-2 text-xs uppercase">Araç</th>
                <th className="text-left px-3 py-2 text-xs uppercase">Şoför</th>
                <th className="text-left px-3 py-2 text-xs uppercase">Kusur</th>
                <th className="text-left px-3 py-2 text-xs uppercase">Durum</th>
                <th className="text-right px-3 py-2 text-xs uppercase">Tutar</th>
                <th className="text-right px-3 py-2 text-xs uppercase">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-slate-400">{busy ? "Yükleniyor..." : "Kayıt yok"}</td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className="border-t hover:bg-slate-50/50" data-testid={`accident-row-${r.id}`}>
                  <td className="px-3 py-2 font-mono">{r.date} {r.time}</td>
                  <td className="px-3 py-2">{r.vehicle_plate || "-"}</td>
                  <td className="px-3 py-2">{r.personnel_name || "-"}</td>
                  <td className="px-3 py-2 text-xs">{r.fault}</td>
                  <td className="px-3 py-2"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${statusColor(r.status)}`}>{r.status}</span></td>
                  <td className="px-3 py-2 text-right font-mono">{fmtTl(r.estimated_cost_tl)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} title="Düzenle" data-testid={`edit-${r.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {r.fault === "Şoför kusurlu" && !r.violation_id && (
                        <Button size="sm" variant="ghost" onClick={() => processDiscipline(r)} title="Disipline İşle" data-testid={`process-${r.id}`}>
                          <Gavel className="w-4 h-4 text-red-700" />
                        </Button>
                      )}
                      {r.violation_id && <span className="text-[10px] text-red-700 font-mono self-center px-1" title="Disipline işlenmiş">✓ Disiplin</span>}
                      <Button size="sm" variant="ghost" onClick={() => downloadPdf(r)} title="PDF" data-testid={`pdf-${r.id}`}>
                        <FileDown className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteItem(r)} title="Sil" data-testid={`delete-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="accident-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <AlertTriangle className="w-5 h-5 text-red-600" /> {editingId ? "Kaza Raporunu Düzenle" : "Yeni Kaza Raporu"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tarih *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="f-date" /></div>
              <div><Label>Saat</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="f-time" /></div>
              <div><Label>Araç *</Label>
                <Select value={form.vehicle_id} onValueChange={(v) => setForm({ ...form, vehicle_id: v })}>
                  <SelectTrigger data-testid="f-vehicle"><SelectValue placeholder="Araç seç" /></SelectTrigger>
                  <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{[v.code, v.plate, v.brand].filter((x) => (x || "").trim()).join(" — ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Şoför * ({driversForDate.length} kayıt)</Label>
                <Select value={form.personnel_id} onValueChange={(v) => setForm({ ...form, personnel_id: v })}>
                  <SelectTrigger data-testid="f-driver"><SelectValue placeholder="Şoför seç (o tarihte çalışan)" /></SelectTrigger>
                  <SelectContent>{driversForDate.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Kaza Yeri</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="f-location" /></div>
              <div className="col-span-2"><Label>Açıklama *</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="f-desc" /></div>
            </div>
            <div className="border rounded p-3 space-y-2 bg-slate-50">
              <div className="flex items-center gap-2">
                <Checkbox id="single" checked={form.single_party} onCheckedChange={(v) => setForm({ ...form, single_party: !!v })} data-testid="f-single" />
                <Label htmlFor="single" className="cursor-pointer font-semibold">Tek taraflı</Label>
              </div>
              {!form.single_party && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input placeholder="Karşı Plaka" value={form.opposing.plate} onChange={(e) => setForm({ ...form, opposing: { ...form.opposing, plate: e.target.value } })} data-testid="f-opp-plate" />
                  <Input placeholder="Ad Soyad" value={form.opposing.name} onChange={(e) => setForm({ ...form, opposing: { ...form.opposing, name: e.target.value } })} />
                  <Input placeholder="Telefon" value={form.opposing.phone} onChange={(e) => setForm({ ...form, opposing: { ...form.opposing, phone: e.target.value } })} />
                  <Input placeholder="Sigorta Şirketi" value={form.opposing.insurance} onChange={(e) => setForm({ ...form, opposing: { ...form.opposing, insurance: e.target.value } })} />
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Kusur *</Label>
                <Select value={form.fault} onValueChange={(v) => setForm({ ...form, fault: v })}>
                  <SelectTrigger data-testid="f-fault"><SelectValue /></SelectTrigger>
                  <SelectContent>{FAULT_OPTIONS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Durum</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="f-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Tahmini Tutar (TL)</Label>
                <Input type="number" step="0.01" value={form.estimated_cost_tl} onChange={(e) => setForm({ ...form, estimated_cost_tl: e.target.value })} data-testid="f-cost" />
              </div>
            </div>
            <div><Label>Hasar Açıklaması</Label><Textarea rows={2} value={form.damage_description} onChange={(e) => setForm({ ...form, damage_description: e.target.value })} data-testid="f-damage" /></div>

            {/* Photos — only after save */}
            {editingItem && (
              <div className="grid grid-cols-2 gap-4 border-t pt-4">
                {["tutanak", "damage"].map((kind) => {
                  const photos = kind === "tutanak" ? (editingItem.tutanak_photos || []) : (editingItem.damage_photos || []);
                  const ref = kind === "tutanak" ? tutanakRef : damageRef;
                  const title = kind === "tutanak" ? "Tutanak Fotoğrafları" : "Kaza / Hasar Fotoğrafları";
                  return (
                    <div key={kind} className="border rounded p-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="font-semibold flex items-center gap-1">
                          {kind === "tutanak" ? <ClipboardList className="w-4 h-4" /> : <Camera className="w-4 h-4" />}
                          {title} ({photos.length})
                        </Label>
                        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0], kind)} data-testid={`f-photo-${kind}`} />
                        <Button size="sm" variant="outline" onClick={() => ref.current?.click()}><Upload className="w-3 h-3 mr-1" /> Yükle</Button>
                      </div>
                      <div className="grid grid-cols-3 gap-1">
                        {photos.map((p) => (
                          <div key={p.pid} className="relative group">
                            <img src={`${process.env.REACT_APP_BACKEND_URL}/api/accidents/${editingItem.id}/photo/${p.pid}`}
                                 alt="" className="w-full h-16 object-cover rounded border" />
                            <button onClick={() => deletePhoto(editingItem.id, p.pid)} className="absolute top-0 right-0 bg-red-600 text-white p-0.5 opacity-0 group-hover:opacity-100 rounded-bl">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div><Label>Notlar</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Kapat</Button>
            <Button onClick={save} className="bg-red-700 hover:bg-red-800 text-white" data-testid="save-accident-btn"><Save className="w-4 h-4 mr-1" /> Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
